import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";

// Marketplace Pages
import { MarketplaceHome } from "./pages/MarketplaceHome";
import { ProductDetails } from "./pages/ProductDetails";
import { ProductsList } from "./pages/ProductsList";
import { Cart } from "./pages/Cart";
import { StoreView } from "./pages/StoreView";
import { StoreInstitutional } from "./pages/StoreInstitutional";
import { NearbyStores } from "./pages/NearbyStores";

// Admin Pages
import { Layout as AdminLayout } from "./components/Layout";
import { Login } from "./pages/Login";
import { ForgotPassword } from "./pages/ForgotPassword";
import { Dashboard } from "./pages/Dashboard";
import { Stores } from "./pages/Stores";
import { StoreDetails } from "./pages/StoreDetails";
import { Products } from "./pages/Products";
import { Categories } from "./pages/Categories";
import { Financial } from "./pages/Financial";
import { Reports } from "./pages/Reports";
import { Settings } from "./pages/Settings";
import { FooterSettings } from "./pages/FooterSettings";
import { OrdersCentral } from "./pages/OrdersCentral";
import { IADestackVendas } from "./pages/IADestackVendas";
import { Reviews } from "./pages/Reviews";
import { DynamicShowcaseAdmin } from "./pages/DynamicShowcaseAdmin";
import { ReviewInvite } from "./pages/ReviewInvite";

// Protected Route for Admin
const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Marketplace Routes */}
      <Route path="/" element={<MarketplaceHome />} />
      <Route path="/produto/:id" element={<ProductDetails />} />
      <Route path="/produtos" element={<ProductsList />} />
      <Route path="/busca" element={<ProductsList />} />
      <Route path="/categoria/:id" element={<ProductsList />} />
      <Route path="/carrinho" element={<Cart />} />
      <Route path="/avaliar/:protocol" element={<ReviewInvite />} />
      <Route path="/loja/:id" element={<StoreView />} />
      <Route path="/loja/:id/institucional" element={<StoreInstitutional />} />
      <Route path="/lojas-proximas" element={<NearbyStores />} />
      <Route path="/lojas" element={<ProductsList />} />
      <Route path="/novidades" element={<ProductsList />} />
      <Route path="/tendencias" element={<ProductsList />} />
      <Route path="/destaques" element={<ProductsList />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/esqueci-senha" element={<ForgotPassword />} />
      <Route path="/recuperar-senha" element={<Navigate to="/esqueci-senha" replace />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><Dashboard /></AdminRoute>} />
      <Route path="/admin/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
      <Route path="/admin/lojas" element={<AdminRoute><Stores /></AdminRoute>} />
      <Route path="/admin/lojas/:id" element={<AdminRoute><StoreDetails /></AdminRoute>} />
      <Route path="/admin/produtos" element={<AdminRoute><Products /></AdminRoute>} />
      <Route path="/admin/categorias" element={<AdminRoute><Categories /></AdminRoute>} />
      <Route path="/admin/financeiro" element={<AdminRoute><Financial /></AdminRoute>} />
      <Route path="/admin/pedidos" element={<AdminRoute><OrdersCentral /></AdminRoute>} />
      <Route path="/admin/avaliacoes" element={<AdminRoute><Reviews /></AdminRoute>} />
      <Route path="/admin/vitrine-dinamica" element={<AdminRoute><DynamicShowcaseAdmin /></AdminRoute>} />
      <Route path="/admin/ia-destack" element={<AdminRoute><IADestackVendas /></AdminRoute>} />
      <Route path="/admin/relatorios" element={<AdminRoute><Reports /></AdminRoute>} />
      <Route path="/admin/configuracoes" element={<AdminRoute><Settings /></AdminRoute>} />
      <Route path="/admin/configuracoes/rodape" element={<AdminRoute><FooterSettings /></AdminRoute>} />
      
      {/* Legacy routes redirect */}
      <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/financeiro" element={<Navigate to="/admin/financeiro" replace />} />
      <Route path="/relatorios" element={<Navigate to="/admin/relatorios" replace />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
